import { useEffect, useMemo, useRef } from 'react';
import { Outlet } from 'react-router-dom';
import {
  Table,
  useFilters,
  useQueryState,
  useScroll,
  usePrevious,
  useGetList,
  IconLoader,
  formatPhone,
  PermissionDenied,
  PermissionEnum,
} from '@ui/src';
import { useAppSelector } from '@store';
import {
  clientsListColumns,
  clientsListColumnsLabels,
  clientsListFiltersSchema,
  clientsListSchema,
} from './variables/list';
import { useLazyGetClientsQuery } from './store/service';
import { Client } from './types';

import cn from '../settings/styles.module.css';

const formatClientsList = (list: Client[]): Client[] => {
  const nextList = list?.map((item) => {
    const result = { ...item };

    const render: Record<string, React.JSX.Element> = {};
    render.email = <div>{result.email || '-'}</div>;
    render.phone = (
      <div>
        {formatPhone(
          result.phone.charAt(0) === '+' ? result.phone : `+${result.phone}`,
        )}
      </div>
    );

    result.render = render;

    return result;
  });

  return nextList;
};

const ClientsList = () => {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const {
    clients: { clients, pagination },
    administrationDictionaries: { activePermissions },
  } = useAppSelector((state) => state);

  const [getList, { isFetching, isLoading }] = useLazyGetClientsQuery();

  const { filters, sort, onSortChange, onFilterChange } =
    useQueryState(clientsListSchema);

  const prevFilters = usePrevious(filters);
  const formattedData = useMemo(() => {
    const data = formatClientsList(clients as Client[]);

    return data;
  }, [clients, sort, filters]);

  const { getListAction, setPage, onFilter, onSort, errorList } =
    useGetList<Client>({
      getList,
      onFilterChange,
      onSortChange,
      schema: clientsListSchema,
    });

  const { filtersComponent, handleSubmit } = useFilters({
    filtersSchema: clientsListFiltersSchema,
    filters,
    onFilterChange: onFilter,
  });

  useScroll({
    element: scrollRef,
    requestInProgress: isFetching,
  });

  useEffect(() => {
    if (
      prevFilters &&
      !Object.keys(prevFilters).length &&
      filters &&
      !clients
    ) {
      getListAction({
        filters,
        sort,
      });
    }
  }, [filters, prevFilters, clients]);

  useEffect(() => {
    let title = 'Users List';

    const companyName = localStorage.getItem('companyName');

    if (companyName) {
      title = `${title} | ${companyName}`;
    }

    document.title = title;
  }, []);

  let content: React.JSX.Element;

  if (activePermissions?.includes(PermissionEnum.SECTION_CUSTOMERS)) {
    if (isLoading) {
      content = (
        <div className={cn.tabLoader}>
          <IconLoader />
        </div>
      );
    } else {
      content = (
        <Table
          columns={clientsListColumns}
          data={formattedData || []}
          onSort={(name) => onSort(name)}
          filters={filters}
          sort={sort}
          columnsLabels={clientsListColumnsLabels}
          onFilterChange={(filterValues) => onFilter(filterValues)}
          filtersComponent={filtersComponent}
          handleSubmit={handleSubmit}
          isLoading={isFetching}
          take="30"
          setPage={(data) => setPage({ ...data, filters })}
          pagination={pagination}
          error={errorList}
          clickable
        />
      );
    }
  } else {
    content = <PermissionDenied />;
  }

  return (
    <div className={cn.currenciesWrapper}>
      <div className={cn.settingsTitle}>Users List</div>
      <div className={cn.contentWrapper} ref={scrollRef}>
        {content}
      </div>
      <Outlet />
    </div>
  );
};

export default ClientsList;
